// apps/web/src/components/FeedbackForm.tsx
import React, { useState, useRef, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { MessageSquare, X, Send, Loader2, Star, MessageCircleHeart, Bug, Lightbulb, AlertCircle, CheckCircle2, Wifi, WifiOff } from 'lucide-react';
import { trpc } from '@/trpc/client';
import { useAuthStore } from '@/store/authStore';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';

export interface FeedbackFormProps {
  isOpen: boolean;
  onClose: () => void;
}

export const FeedbackForm: React.FC<FeedbackFormProps> = ({ isOpen, onClose }) => {
  const [rating, setRating] = useState<number | null>(null);
  const [hoveredStar, setHoveredStar] = useState<number | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const { user } = useAuthStore();
  const firstInputRef = useRef<HTMLInputElement>(null);

  const [formData, setFormData] = useState({
    name: user?.profile?.fullName || '',
    email: user?.email || '',
    type: 'general' as 'general' | 'bug' | 'feature_request' | 'complaint',
    subject: '',
    message: '',
  });

  // Monitor online/offline status
  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Auto-focus first input when modal opens
  useEffect(() => {
    if (isOpen && firstInputRef.current) {
      setTimeout(() => firstInputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  const { mutate: submitFeedback } = trpc.feedback.submitFeedback.useMutation({
    onSuccess: () => {
      toast.success('Thank you for your feedback!');
      setFormData({
        name: user?.profile?.fullName || '',
        email: user?.email || '',
        type: 'general',
        subject: '',
        message: '',
      });
      setRating(null);
      onClose();
      setIsSubmitting(false);
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to submit feedback');
      setIsSubmitting(false);
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!isOnline) {
      toast.error('No internet connection. Please check your network.');
      return;
    }

    if (!formData.name.trim()) {
      toast.error('Please enter your name');
      return;
    }

    if (!formData.email.trim()) {
      toast.error('Please enter your email');
      return;
    }

    if (formData.subject.trim().length < 5) {
      toast.error('Subject must be at least 5 characters');
      return;
    }

    if (formData.message.trim().length < 10) {
      toast.error('Message must be at least 10 characters');
      return;
    }

    setIsSubmitting(true);
    submitFeedback({
      ...formData,
      rating,
    });
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'bug': return <Bug className="w-4 h-4" />;
      case 'feature_request': return <Lightbulb className="w-4 h-4" />;
      case 'complaint': return <AlertCircle className="w-4 h-4" />;
      default: return <MessageCircleHeart className="w-4 h-4" />;
    }
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'bug': return 'from-red-500 to-orange-500';
      case 'feature_request': return 'from-blue-500 to-cyan-500';
      case 'complaint': return 'from-yellow-500 to-amber-500';
      default: return 'from-purple-500 to-pink-500';
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: 20, scale: 0.95 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: 20, scale: 0.95 }}
        transition={{ type: "spring", stiffness: 300, damping: 30 }}
        className="fixed inset-0 sm:inset-auto sm:bottom-24 sm:right-4 md:right-8 z-50 flex items-end sm:items-center justify-center sm:justify-end p-0 sm:p-4"
      >
        {/* Mobile backdrop */}
        <div
          className="absolute inset-0 bg-black/50 sm:hidden"
          onClick={onClose}
        />

        <Card className="relative w-full h-[100dvh] sm:h-auto sm:w-[380px] md:w-[420px] sm:max-h-[90vh] overflow-hidden shadow-2xl rounded-none sm:rounded-2xl border-0 sm:border-2 border-primary/20 bg-gradient-to-br from-background via-background to-purple-500/5 backdrop-blur-xl">
          {/* Animated Header with Gradient */}
          <CardHeader className={`bg-gradient-to-r ${getTypeColor(formData.type)} text-white relative overflow-hidden`}>
            {/* Animated background pattern */}
            <div className="absolute inset-0 opacity-20">
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_120%,rgba(255,255,255,0.3),transparent_50%)]" />
            </div>

            <div className="flex items-center justify-between gap-2 relative z-10">
              <div className="flex items-center gap-2 sm:gap-3 min-w-0">
                <div className="relative">
                  <div className="absolute inset-0 bg-white/20 rounded-full blur-md animate-pulse" />
                  <div className="relative bg-white/10 backdrop-blur-sm p-2 sm:p-2.5 rounded-full border border-white/20">
                    {getTypeIcon(formData.type)}
                  </div>
                </div>
                <div className="min-w-0">
                  <CardTitle className="text-white text-sm sm:text-base font-bold truncate">Share Your Feedback</CardTitle>
                  <CardDescription className="text-white/90 text-[10px] sm:text-xs font-medium truncate">Help us improve your experience</CardDescription>
                </div>
              </div>
              <button
                onClick={onClose}
                className="hover:bg-white/20 p-1.5 sm:p-2 rounded-lg transition-all hover:scale-110 active:scale-95 flex-shrink-0 touch-manipulation"
                title="Close feedback form"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </CardHeader>

          {/* Offline Warning */}
          {!isOnline && (
            <div className="bg-yellow-500/10 border-b border-yellow-500/20 px-4 py-2 flex items-center gap-2 text-yellow-600 dark:text-yellow-400">
              <WifiOff className="w-4 h-4" />
              <span className="text-xs font-medium">No internet connection</span>
            </div>
          )}

          {/* Scrollable Content */}
          <div className="overflow-y-auto flex-1 sm:max-h-[calc(90vh-140px)] custom-scrollbar overscroll-contain">
            <CardContent className="p-4 sm:p-5 space-y-3 sm:space-y-4">
              <style dangerouslySetInnerHTML={{
                __html: `
                .custom-scrollbar::-webkit-scrollbar {
                  width: 4px;
                }
                .custom-scrollbar::-webkit-scrollbar-track {
                  background: transparent;
                }
                .custom-scrollbar::-webkit-scrollbar-thumb {
                  background: hsl(var(--primary) / 0.3);
                  border-radius: 3px;
                }
                .custom-scrollbar::-webkit-scrollbar-thumb:hover {
                  background: hsl(var(--primary) / 0.5);
                }
              `}} />

              <form onSubmit={handleSubmit} className="space-y-3 sm:space-y-4">
                {/* Feedback Type - Prominent */}
                <div className="space-y-2">
                  <Label htmlFor="type" className="text-xs sm:text-sm font-semibold flex items-center gap-2">
                    <MessageSquare className="w-3 h-3 sm:w-4 sm:h-4 text-primary" />
                    Feedback Type
                  </Label>
                  <Select
                    value={formData.type}
                    onValueChange={(value) =>
                      setFormData({
                        ...formData,
                        type: value as typeof formData.type,
                      })
                    }
                    disabled={isSubmitting}
                  >
                    <SelectTrigger className="h-10 sm:h-11 rounded-xl border-border/50 bg-background/50 backdrop-blur-sm focus:border-primary/50 focus:ring-2 focus:ring-primary/20 text-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="general">
                        <div className="flex items-center gap-2">
                          <MessageCircleHeart className="w-4 h-4 text-purple-500" />
                          General Feedback
                        </div>
                      </SelectItem>
                      <SelectItem value="bug">
                        <div className="flex items-center gap-2">
                          <Bug className="w-4 h-4 text-red-500" />
                          Bug Report
                        </div>
                      </SelectItem>
                      <SelectItem value="feature_request">
                        <div className="flex items-center gap-2">
                          <Lightbulb className="w-4 h-4 text-blue-500" />
                          Feature Request
                        </div>
                      </SelectItem>
                      <SelectItem value="complaint">
                        <div className="flex items-center gap-2">
                          <AlertCircle className="w-4 h-4 text-yellow-500" />
                          Complaint
                        </div>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Name & Email Row */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label htmlFor="name" className="text-xs sm:text-sm font-medium">Your Name</Label>
                    <Input
                      ref={firstInputRef}
                      id="name"
                      type="text"
                      placeholder="John Doe"
                      value={formData.name}
                      onChange={(e) =>
                        setFormData({ ...formData, name: e.target.value })
                      }
                      disabled={isSubmitting}
                      className="h-10 sm:h-11 text-sm rounded-xl border-border/50 bg-background/50 backdrop-blur-sm focus:border-primary/50 focus:ring-2 focus:ring-primary/20 touch-manipulation"
                      autoComplete="name"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-xs sm:text-sm font-medium">Email</Label>
                    <Input
                      id="email"
                      type="email"
                      placeholder="john@example.com"
                      value={formData.email}
                      onChange={(e) =>
                        setFormData({ ...formData, email: e.target.value })
                      }
                      disabled={isSubmitting}
                      className="h-10 sm:h-11 text-sm rounded-xl border-border/50 bg-background/50 backdrop-blur-sm focus:border-primary/50 focus:ring-2 focus:ring-primary/20 touch-manipulation"
                      autoComplete="email"
                    />
                  </div>
                </div>

                {/* Subject */}
                <div className="space-y-2">
                  <Label htmlFor="subject" className="text-xs sm:text-sm font-medium">Subject</Label>
                  <Input
                    id="subject"
                    type="text"
                    placeholder="Brief description"
                    value={formData.subject}
                    onChange={(e) =>
                      setFormData({ ...formData, subject: e.target.value })
                    }
                    disabled={isSubmitting}
                    className="h-10 sm:h-11 text-sm rounded-xl border-border/50 bg-background/50 backdrop-blur-sm focus:border-primary/50 focus:ring-2 focus:ring-primary/20 touch-manipulation"
                  />
                </div>

                {/* Message */}
                <div className="space-y-2">
                  <Label htmlFor="message" className="text-xs sm:text-sm font-medium">Your Message</Label>
                  <Textarea
                    id="message"
                    placeholder="Tell us more about your experience..."
                    value={formData.message}
                    onChange={(e) =>
                      setFormData({ ...formData, message: e.target.value })
                    }
                    disabled={isSubmitting}
                    rows={3}
                    className="resize-none text-sm rounded-xl border-border/50 bg-background/50 backdrop-blur-sm focus:border-primary/50 focus:ring-2 focus:ring-primary/20 touch-manipulation"
                  />
                </div>

                {/* Star Rating with Enhanced Interaction */}
                <div className="space-y-2 sm:space-y-3 p-3 sm:p-4 bg-gradient-to-br from-muted/50 to-primary/5 rounded-2xl border border-border/30">
                  <Label className="text-xs sm:text-sm font-semibold flex items-center gap-2">
                    <Star className="w-3 h-3 sm:w-4 sm:h-4 text-yellow-500 fill-yellow-500" />
                    Rate Your Experience
                  </Label>
                  <div className="flex gap-1 sm:gap-2 justify-center">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <motion.button
                        key={star}
                        type="button"
                        onClick={() => setRating(star)}
                        onMouseEnter={() => setHoveredStar(star)}
                        onMouseLeave={() => setHoveredStar(null)}
                        disabled={isSubmitting}
                        whileHover={{ scale: 1.15 }}
                        whileTap={{ scale: 0.95 }}
                        className={`p-1 sm:p-1.5 rounded-lg transition-all touch-manipulation ${(hoveredStar !== null ? star <= hoveredStar : rating && rating >= star)
                          ? 'text-yellow-400'
                          : 'text-gray-300 hover:text-yellow-300'
                          }`}
                      >
                        <Star className={`w-6 h-6 sm:w-7 sm:h-7 transition-all ${(hoveredStar !== null ? star <= hoveredStar : rating && rating >= star)
                          ? 'fill-current drop-shadow-[0_0_8px_rgba(234,179,8,0.5)]'
                          : ''
                          }`} />
                      </motion.button>
                    ))}
                  </div>
                  {rating && (
                    <motion.p
                      initial={{ opacity: 0, y: -5 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="text-center text-xs sm:text-sm text-muted-foreground font-medium"
                    >
                      {rating === 5 ? '🎉 Excellent!' : rating >= 4 ? '😊 Great!' : rating === 3 ? '😐 Good' : rating === 2 ? '😕 Fair' : '😞 Poor'}
                    </motion.p>
                  )}
                </div>

                {/* Submit Button */}
                <Button
                  type="submit"
                  disabled={isSubmitting || !isOnline}
                  className={`w-full h-11 sm:h-12 text-sm font-semibold rounded-xl shadow-lg bg-gradient-to-r ${getTypeColor(formData.type)} hover:opacity-90 transition-all hover:scale-[1.02] active:scale-98 disabled:opacity-50 disabled:cursor-not-allowed touch-manipulation`}
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send className="mr-2 h-4 w-4" />
                      Submit Feedback
                    </>
                  )}
                </Button>

                {/* Thank You Message */}
                <div className="flex items-center justify-center gap-2 pt-1 sm:pt-2">
                  <CheckCircle2 className="w-3 h-3 sm:w-4 sm:h-4 text-green-500" />
                  <p className="text-[10px] sm:text-xs text-muted-foreground text-center font-medium">
                    Your feedback helps us improve 🙏
                  </p>
                </div>
              </form>
            </CardContent>
          </div>
        </Card>
      </motion.div>
    </AnimatePresence>
  );
};

export default FeedbackForm;
